import React, {
  RefObject,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Row,
} from '@tanstack/react-table';
import Table from 'core/components/VirtualTable';
import isNil from 'lodash/isNil';
import {
  useTableReset,
  useConfirmDialog,
  useNotify,
} from 'core/hooks';
import {
  VirtualTableInstance,
  VirtualTableColumnDef as ColumnDef,
} from 'core/components/VirtualTable/types';
import IconButton from '@mui/material/IconButton';
import DuplicateIcon from '@mui/icons-material/ControlPointDuplicateRounded';
import DeleteIcon from 'core/icons/delete';
import {
  settingsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  FormViewDTO,
  FormCategoryDTO,
} from 'dtos';
import {
  FormCategory,
} from 'dtos/formCategory';
import {
  formatDateTime,
} from 'utils/date';
import Name from 'pages/Dashboard/components/Name';
import Tag from 'core/components/Tag';
import CreateDialog from 'views/EMR/Settings/Forms/CreateDialog';
import CopyDialog from 'views/EMR/Settings/Forms/CopyDialog';
import PdfTemplateBuilder from 'views/EMR/Settings/PdfTemplateBuilder';
import {
  getAllFormsByOrg,
} from 'views/EMR/Settings/transport';
import SearchIcon from 'core/icons/search';
import SearchInput from 'pages/Dashboard/components/SearchInput';
import {
  useDeleteForm,
  useGetFormCategories,
} from 'views/EMR/Settings/PdfTemplateBuilder/hooks';
import ToolTip from 'core/components/Tooltip';
import {
  useSyncSearchQuery,
} from 'pages/Dashboard/hooks/url';
import {
  PdfTemplateBuilderLocationState,
} from 'views/EMR/Settings/PdfTemplateBuilder/types';
import clsx from 'clsx';
import TabFilter from 'views/EMR/components/TabFilter';
import AddIcon from '@mui/icons-material/Add';
import Button from '@populateui/ui/button';
import {
  DEFAULT_EMPTY_VALUE,
} from 'core/constants';
import useEMRAccess from 'views/EMR/hooks/useEMRAccess';
import {
  useUserProfilePicturePath,
} from 'pages/Dashboard/hooks/media';

type UpdatedByUserCellProps = {
  row: FormViewDTO;
};

function UpdatedByUserCell({ row }: UpdatedByUserCellProps) {
  const src = useUserProfilePicturePath(row.updatedByUser?.userId);

  return (
    <Name
      image={isNil(row.updatedByUser) ? undefined : { size: 'xs', src }}
      className="min-w-0"
      {...(row.updatedByUser?.formattedNames ?? {})}
      placeholder={DEFAULT_EMPTY_VALUE}
    />
  );
}

type FormActionsCellProps = {
  row: Row<FormViewDTO>;
  onCopy: (form: FormViewDTO) => void;
  canDuplicate: boolean;
};

function FormActionsCell({ row, onCopy, canDuplicate }: FormActionsCellProps) {
  const { mutateAsync: deleteForm } = useDeleteForm();
  const notify = useNotify();
  const { confirm, closeConfirm } = useConfirmDialog();

  return (
    <div className="flex items-center">
      {canDuplicate && (
        <ToolTip title="Duplicate">
          <IconButton
            aria-label="Duplicate Form"
            size="small"
            className="!p-1"
            onClick={(e) => {
              e.stopPropagation();
              onCopy(row.original);
            }}
          >
            <DuplicateIcon className="!w-5 !h-5 !text-black" />
          </IconButton>
        </ToolTip>
      )}
      <ToolTip title="Archive">
      <IconButton
        aria-label="Archive Template"
        size="small"
        className="!p-1"
        onClick={async (e) => {
          e.stopPropagation();
          confirm({
            content: `Are you sure you want to archive ${row.original.title}?`,
            title: 'Archive',
            confirmText: 'Archive',
            onConfirm: async () => {
              await deleteForm({ id: row.original.formId });
              notify({
                variant: 'success',
                message: 'Form was archived successfully',
              });
              settingsEventChannel.emit('onChangeTemplates');
              closeConfirm();
            },
          });
        }}
        color="error"
      >
        <DeleteIcon className="!w-5 !h-5 !text-black" />
      </IconButton>
    </ToolTip>
    </div>
  );
}

type CopyDialogParams = {
  formId: number;
  title: string;
  category?: FormCategoryDTO | null;
};

export type CreateDialogParams = {
  category?: FormCategory | null;
};

type BuilderDialogParams = {
  formId: number;
  builderState?: PdfTemplateBuilderLocationState | null;
};

export default function Forms() {
  const { tableInstanceRef, reset } = useTableReset();
  const { isRestricted } = useEMRAccess();
  const {
    data: formCategories = [],
  } = useGetFormCategories();

  const [
    createFormParams,
    setCreateFormParams,
  ] = useState<CreateDialogParams | null>(null);
  const [
    formBuilderParams,
    setFormBuilderParams,
  ] = useState<BuilderDialogParams | null>(null);
  const [
    copyFormParams,
    setCopyFormParams,
  ] = useState<CopyDialogParams | null>(null);
  const {
    searchQuery,
    setSearchQuery,
  } = useSyncSearchQuery();
  const [formCategory, setFormCategory] = useState<FormCategory | null>(null);
  const visibleFormCategories = useMemo(
    () => (
      isRestricted
        ? formCategories
        : [{ name: 'All', value: null }, ...formCategories]
    ),
    [formCategories, isRestricted],
  );
  const formCategoryTabs = useMemo(
    () => visibleFormCategories.map((category) => category.name),
    [visibleFormCategories],
  );
  const selectedCategoryTab = useMemo(
    () => (
      visibleFormCategories?.find((category) => category.value === formCategory)?.name
      ?? 'All'
    ),
    [formCategory, visibleFormCategories],
  );

  const columns = useMemo<ColumnDef<FormViewDTO>[]>(() => [{
    id: 'title',
    accessorKey: 'title',
    columnTitle: 'Title',
    meta: { autoSized: true },
  }, {
    id: 'category',
    columnTitle: 'Category',
    meta: { autoSized: true },
    cell: ({ row }) => (
      <div>{row.original.category?.name ?? '--'}</div>
    ),
  }, {
    id: 'status',
    columnTitle: 'Status',
    meta: { autoSized: true },
    cell: ({ row: { original: { isDraft } } }) => (
      <Tag
        tagName={isDraft ? 'Draft' : 'Published'}
        defaultColor={clsx(
          {
            'bg-gray-200': isDraft,
            'bg-green-100': !isDraft,
          },
        )}
        className="!font-semibold !text-gray-700"
      />
    ),
  }, {
    id: 'updatedByUser',
    columnTitle: 'Updated By',
    meta: { autoSized: true },
    cell: ({ row }) => <UpdatedByUserCell row={row.original} />,
  }, {
    id: 'updatedOn',
    columnTitle: 'Updated On',
    meta: { autoSized: true },
    cell: ({ row: { original } }) => (!isNil(original?.updatedOn) ? formatDateTime(original.updatedOn) : '--'),
  }, {
    id: 'actions',
    header: '',
    size: 80,
    cell: ({ row }) => (
      <FormActionsCell
        canDuplicate={!isRestricted}
        row={row}
        onCopy={(form) => {
          if (!isNil(form.formId) && !isNil(form.title)) {
            setCopyFormParams({
              formId: form.formId,
              title: form.title,
              category: form.category,
            });
          }
        }}
      />
    ),
  }], []);

  useEffect(() => {
    const unsubscribeFromCreateForm = settingsEventChannel.on('onCreateTemplate', (params) => {
      setCreateFormParams(params);
    });
    const unsubscribeFromTemplatesChange = settingsEventChannel.on('onChangeTemplates', () => {
      tableInstanceRef?.current?.refetch?.();
    });

    return () => {
      unsubscribeFromCreateForm();
      unsubscribeFromTemplatesChange();
    };
  }, []);

  return (
    <div className="h-full w-full flex flex-col gap-4">
      <div className="flex flex-col gap-4 px-4">
        <div className="flex justify-between gap-4">
          <div className="flex w-full gap-4">
            <SearchInput
              key="form-search"
              placeholder="Enter Form Name..."
              className="w-1/3 h-7"
              value={searchQuery}
              onChange={(value) => {
                setSearchQuery(value);
                reset();
              }}
              icon={<SearchIcon className="!w-4 !h-4 text-gray-400" />}
              id="form-search"
            />
            <div className="flex items-center gap-2 justify-between">
              <TabFilter
                key={selectedCategoryTab}
                options={formCategoryTabs}
                defaultTab={selectedCategoryTab}
                onUpdate={(tab) => {
                  const nextCategory = visibleFormCategories.find(
                    (category) => category.name === tab,
                  )?.value as FormCategory | null;
                  setFormCategory(nextCategory ?? null);
                  reset();
                }}
                size="medium"
                theme="color"
              />
            </div>
          </div>
          <Button
            className="!h-8"
            color="primary"
            variant="contained"
            startIcon={<AddIcon className="!w-4 !h-4" />}
            onClick={() => setCreateFormParams({ category: formCategory })}
          >
            Add Form
          </Button>
        </div>
      </div>
      <div className=" relative flex flex-col flex-1 overflow-y-hidden">
        <div className="h-full w-full">
          <Table<FormViewDTO>
            defaultPagination={{
              pageSize: 25,
              pageIndex: 0,
            }}
            queryParams={{
              searchQuery: searchQuery.trim(),
              formCategory,
            }}
            dataKey="forms-table-data"
            instanceRef={tableInstanceRef as RefObject<VirtualTableInstance>}
            getData={getAllFormsByOrg}
            getRowId={(row) => row?.formId}
            columns={columns}
            emptySlot="No Forms Found"
            classes={{
              header: 'px-4',
              scrollContainer: 'justify-center items-center',
              outerRow: '!w-[calc(100%-16px)] left-4',
              row: '!items-center py-1',
            }}
            onRowClick={(table, row) => {
              if (!isNil(row.original.formId)) {
                setFormBuilderParams({
                  formId: row.original.formId,
                });
              }
            }}
          />
        </div>
      </div>
      {!isNil(createFormParams) && (
        <CreateDialog
          {...createFormParams}
          onClose={() => { setCreateFormParams(null); }}
          onOpenBuilder={(params) => {
            setFormBuilderParams(params);
          }}
        />
      )}
      {!isNil(copyFormParams) && (
        <CopyDialog
          sourceFormId={copyFormParams.formId}
          sourceTitle={copyFormParams.title}
          sourceCategory={copyFormParams.category}
          onClose={() => { setCopyFormParams(null); }}
          onOpenBuilder={(formId) => {
            setFormBuilderParams({ formId });
          }}
        />
      )}
      {!isNil(formBuilderParams) && (
        <PdfTemplateBuilder
          formId={formBuilderParams.formId}
          builderState={formBuilderParams.builderState}
          onClose={() => { setFormBuilderParams(null); }}
        />
      )}
    </div>
  );
}
